import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Keyboard, ArrowLeft, Trophy, History, Zap, CheckCircle2, AlertCircle, Timer, Award
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DIFFICULTY_LEVELS = [
  { id: 'beginner', name: 'Beginner', desc: 'Standard vocabulary, lowercase', color: 'text-green-400', border: 'border-green-500/20', bg: 'bg-green-500/10' },
  { id: 'intermediate', name: 'Intermediate', desc: 'Mixed case, punctuation, numbers', color: 'text-yellow-400', border: 'border-yellow-500/20', bg: 'bg-yellow-500/10' },
  { id: 'advanced', name: 'Advanced', desc: 'Technical symbols, code snippets', color: 'text-red-400', border: 'border-red-500/20', bg: 'bg-red-500/10' }
];

const SCRIPTS = {
  beginner: [
    "The quick brown fox jumps over the lazy dog in the park. The sun is shining brightly today and the weather is very warm.",
    "Many people like to buy things online from a shop. You can search for what you want and add it to your cart.",
    "A clean and simple web page is often the best design. Users like to see menus that are easy to click and read.",
    "He read a new comic about a hero who travels back in time. The art is great and the story is very fun to read every week.",
    "Phones are getting faster and have better screens now. You can take great photos and play games all day long."
  ],
  intermediate: [
    "When running an A/B test, it is crucial to monitor the control and variant groups closely. Have we reached statistical significance after gathering 10,000 users?",
    "Improving the search experience for users in the EU and GB regions requires careful analysis. If search depth increases by 15%, does it actually lead to a higher repurchase rate?",
    "\"Regressing with the King's Power\" and \"Demonic Evolution\" are updated every Friday! It is always exciting to see how the main character levels up their abilities.",
    "Launching a new web application involves setting up targeted ad campaigns. For example, focusing on English-speaking countries might yield a better return on ad spend.",
    "Global energy markets are heavily influenced by the dynamic relationship between the US and China. Shifts in trade policies can trigger rapid changes in pricing."
  ],
  advanced: [
    "SELECT user_id, COUNT(DISTINCT item_id) AS unique_clicks FROM search_logs WHERE region IN ('EU', 'GB', 'JP') AND event_date >= '2025-11-01' GROUP BY user_id HAVING unique_clicks > 5;",
    "df_merged = pd.merge(df_users, df_transactions, on='user_id', how='left') rpd_30 = df_merged[df_merged['days_since_first_purchase'] <= 30]['is_repurchased'].mean() * 100",
    "gcloud run deploy pollar-ideation-app --image gcr.io/my-project/pollar-app:v1.0.4 --region asia-southeast1 --allow-unauthenticated --max-instances 10",
    "The Q4 analysis revealed that Black Friday GMV spiked by 34.7% (YoY), while the Repurchase Days (RPD30) metric showed a variance of +/- 2.3% across the p90 cohort.",
    "{\"app_name\": \"Swift Keys\", \"version\": \"1.0.0\", \"settings\": {\"theme\": \"minimal-dark\", \"max_wpm_threshold\": 150, \"require_exact\": true, \"api_endpoints\": [\"/api/v1/score\"]}}"
  ]
};

export default function SwiftKeysPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();
  
  // Game State
  const [gameState, setGameState] = useState('menu'); // menu, playing, intermission, results
  const [levelIndex, setLevelIndex] = useState(0);
  const [targetScript, setTargetScript] = useState('');
  const [input, setInput] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [liveStats, setLiveStats] = useState({ wpm: 0, accuracy: 100 });
  const [finalResult, setFinalResult] = useState(null);
  const [runData, setRunData] = useState([]); // Stores { target, typed, time } for each level

  // Data
  const [leaderboard, setLeaderboard] = useState([]);
  const [attempts, setAttempts] = useState([]);
  
  // Refs
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    fetchLeaderboard();
    fetchAttempts();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/swift-keys/leaderboard`);
      setLeaderboard(response.data);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
    }
  };

  const fetchAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/swift-keys/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempts(response.data);
    } catch (e) {
      console.error('Failed to fetch attempts', e);
    }
  };

  const startCampaign = () => {
    setRunData([]);
    setLevelIndex(0);
    startLevel(0);
  };

  const startLevel = (index) => {
    const levelId = DIFFICULTY_LEVELS[index].id;
    const pool = SCRIPTS[levelId];
    const script = pool[Math.floor(Math.random() * pool.length)];
    
    setTargetScript(script);
    setInput('');
    setStartTime(null);
    setLiveStats({ wpm: 0, accuracy: 100 });
    setGameState('playing');
    
    // Focus input after render
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleNextLevel = () => {
    const nextIndex = levelIndex + 1;
    setLevelIndex(nextIndex);
    startLevel(nextIndex);
  };

  const handleInput = (e) => {
    const val = e.target.value;
    
    // Start timer on first char
    if (!startTime && val.length > 0) {
      setStartTime(Date.now());
    }
    
    setInput(val);
    
    // Calculate live stats
    if (startTime) {
      const timeMin = (Date.now() - startTime) / 60000;
      const wpm = timeMin > 0 ? (val.length / 5) / timeMin : 0;
      
      let correct = 0;
      for (let i = 0; i < val.length; i++) {
        if (val[i] === targetScript[i]) correct++;
      }
      const acc = val.length > 0 ? (correct / val.length) * 100 : 100;
      
      setLiveStats({ wpm: Math.round(wpm), accuracy: Math.round(acc) });
    }

    // Check completion
    if (val.length === targetScript.length) {
      finishLevel(val);
    }
  };

  const finishLevel = (finalInput) => {
    const end = Date.now();
    const timeTaken = (end - (startTime || Date.now())) / 1000;
    
    const newRunData = [...runData, {
        target: targetScript,
        typed: finalInput,
        time: timeTaken
    }];
    setRunData(newRunData);

    if (levelIndex < 2) {
        setGameState('intermission');
    } else {
        // Ensure state update happens before async call
        submitCampaign(newRunData);
    }
  };

  const submitCampaign = async (finalRunData) => {
    // Show loading state immediately to prevent "stall" feeling
    setGameState('results'); 
    // We can use a temporary "calculating..." state if we want, 
    // but jumping to results with null result will show empty screen.
    // Better to let the UI handle null finalResult or show a loader.
    
    // Aggregate stats preserving alignment for backend logic
    let combinedTarget = "";
    let combinedTyped = "";
    let totalTime = 0;

    finalRunData.forEach(level => {
        const maxLen = Math.max(level.target.length, level.typed.length);
        // Pad with non-matching chars to ensure alignment errors are counted correctly
        const padT = level.target.padEnd(maxLen, '\u0001');
        const padU = level.typed.padEnd(maxLen, '\u0002');
        
        combinedTarget += padT;
        combinedTyped += padU;
        totalTime += level.time;
    });

    try {
      const token = localStorage.getItem('token');
      // If user is not logged in, calculate locally
      if (!token) {
        setFinalResult({
            gross_wpm: 0, 
            net_wpm: 0, 
            accuracy: 0, 
            final_score: 0 
        });
        toast.info("Please sign in to save scores!");
        return;
      }

      const response = await axios.post(`${API}/swift-keys/results`, {
        target_script: combinedTarget,
        typed_script: combinedTyped,
        time_taken: totalTime,
        difficulty: 'Marathon'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFinalResult(response.data);
      if (user) {
        fetchLeaderboard();
        fetchAttempts();
        toast.success("Campaign Complete!");
      }
    } catch (e) {
      console.error(e);
      // Fallback for error to stop spinning
      setFinalResult({
        gross_wpm: 0,
        net_wpm: 0,
        accuracy: 0,
        final_score: 0
      });
      toast.error("Failed to save score");
    }
  };

  // Render text with highlighting
  const renderText = () => {
    return targetScript.split('').map((char, i) => {
      let className = "text-slate-500";
      if (i < input.length) {
        className = input[i] === char ? "text-white" : "text-red-500 bg-red-500/20";
      } else if (i === input.length) {
        className = "bg-indigo-500 text-white animate-pulse"; // Cursor
      }
      
      return (
        <span key={i} className={className}>{char}</span>
      );
    });
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans selection:bg-indigo-500/30">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 p-4 flex justify-between items-center bg-[#020617]/90 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" /> Exit
            </Button>
            <div className="text-sm text-slate-400 hidden sm:block">
                {user?.username}
            </div>
        </div>
        <div className="flex items-center gap-2 font-heading font-bold text-lg text-indigo-400">
            <Keyboard className="w-5 h-5" />
            <span>SWIFT KEYS</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Game Area */}
            <div className={`${isMobile ? 'w-full' : 'lg:col-span-2'}`}>
                <div className="flex flex-col items-center justify-center p-6 relative w-full bg-slate-900/30 rounded-3xl border border-slate-800/50 min-h-[500px]">
                    
                    {/* Menu State */}
                    {gameState === 'menu' && (
                        <div className="w-full max-w-md space-y-8 animate-in fade-in zoom-in duration-500">
                            <div className="text-center space-y-4">
                                <div className="w-20 h-20 bg-indigo-500/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                    <Keyboard className="w-10 h-10 text-indigo-400" />
                                </div>
                                <h1 className="text-4xl font-bold tracking-tight">Typing Challenge</h1>
                                <p className="text-slate-400">Complete 3 levels of increasing difficulty.</p>
                            </div>
                            
                            <Button onClick={startCampaign} className="w-full py-8 text-xl font-bold bg-indigo-600 hover:bg-indigo-500 rounded-xl shadow-lg shadow-indigo-500/20">
                                Start Campaign
                            </Button>

                            <div className="grid grid-cols-3 gap-2 text-center text-xs text-slate-500">
                                {DIFFICULTY_LEVELS.map(l => (
                                    <div key={l.id} className="bg-slate-800/50 p-2 rounded border border-slate-700">
                                        <div className={l.color}>{l.name}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Playing State */}
                    {gameState === 'playing' && (
                        <div className="w-full max-w-2xl space-y-8 animate-in fade-in duration-300">
                            <div className="flex justify-between items-center text-sm font-mono text-slate-400">
                                <div className="flex items-center gap-2">
                                    <span className="bg-slate-800 px-2 py-1 rounded text-white">Stage {levelIndex + 1}/3</span>
                                    <span className={DIFFICULTY_LEVELS[levelIndex].color}>{DIFFICULTY_LEVELS[levelIndex].name}</span>
                                </div>
                                <div className="flex gap-4">
                                    <span className="text-indigo-400 font-bold">{liveStats.wpm} WPM</span>
                                    <span className={liveStats.accuracy < 90 ? 'text-red-400' : 'text-emerald-400'}>{liveStats.accuracy}% Acc</span>
                                </div>
                            </div>

                            <div 
                                className="font-mono text-xl sm:text-2xl leading-relaxed tracking-wide min-h-[200px] select-none"
                                onClick={() => inputRef.current?.focus()}
                            >
                                {renderText()}
                            </div>

                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={handleInput}
                                className="opacity-0 absolute top-0 left-0 h-full w-full cursor-default"
                                autoFocus
                                onBlur={(e) => setTimeout(() => e.target.focus(), 10)} // Keep focus
                            />
                            
                            <div className="text-center text-slate-500 text-sm">
                                Start typing to begin...
                            </div>
                        </div>
                    )}

                    {/* Intermission State */}
                    {gameState === 'intermission' && (
                        <div className="text-center space-y-8 animate-in zoom-in duration-300 w-full max-w-md">
                            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                            </div>
                            <h2 className="text-3xl font-bold">Stage {levelIndex + 1} Complete!</h2>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                                    <div className="text-sm text-slate-500 uppercase">WPM</div>
                                    <div className="text-2xl font-bold text-white">{liveStats.wpm}</div>
                                </div>
                                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                                    <div className="text-sm text-slate-500 uppercase">Accuracy</div>
                                    <div className="text-2xl font-bold text-emerald-400">{liveStats.accuracy}%</div>
                                </div>
                            </div>

                            <Button onClick={handleNextLevel} className="w-full py-6 font-bold bg-white text-black hover:bg-slate-200">
                                Next Level: {DIFFICULTY_LEVELS[levelIndex + 1].name} <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />
                            </Button>
                        </div>
                    )}

                    {/* Results State */}
                    {gameState === 'results' && (
                        <div className="text-center space-y-8 animate-in zoom-in duration-500 w-full max-w-md">
                            {!finalResult ? (
                                <div className="space-y-4">
                                    <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                                    <h2 className="text-2xl font-bold animate-pulse">Calculating Score...</h2>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-bounce" />
                                        <h2 className="text-3xl font-bold">Campaign Complete!</h2>
                                        <div className="text-slate-400 font-mono">
                                            Marathon Mode
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                                            <div className="text-sm text-slate-500 uppercase tracking-wider mb-1">Net WPM</div>
                                            <div className="text-3xl font-bold text-white">{finalResult.net_wpm}</div>
                                        </div>
                                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                                            <div className="text-sm text-slate-500 uppercase tracking-wider mb-1">Accuracy</div>
                                            <div className="text-3xl font-bold text-emerald-400">{finalResult.accuracy}%</div>
                                        </div>
                                        <div className="col-span-2 bg-indigo-500/10 p-6 rounded-xl border border-indigo-500/20">
                                            <div className="text-sm text-indigo-300 uppercase tracking-wider mb-2">Final Score</div>
                                            <div className="text-5xl font-black text-indigo-400">{finalResult.final_score}</div>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 justify-center">
                                        <Button onClick={() => setGameState('menu')} className="w-full py-6 font-bold bg-white text-black hover:bg-slate-200">
                                            Try Again
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Leaderboard */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Global Leaderboard</h3>
                </div>
                <ScrollArea className="h-64">
                  {leaderboard.length > 0 ? (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            entry.username === user?.username ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {entry.rank}
                            </span>
                            <span className="font-medium truncate max-w-24">{entry.username}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-indigo-400">{entry.score}</div>
                            <div className="text-xs text-slate-500">{Math.round(entry.wpm)} WPM</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No entries yet. Be the first!
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recent Attempts */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-heading font-bold text-lg">Your History</h3>
                </div>
                <ScrollArea className="h-64">
                  {attempts.length > 0 ? (
                    <div className="space-y-2">
                      {attempts.slice(0, 15).map((attempt, idx) => (
                        <div 
                          key={attempt.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                        >
                          <div>
                            <div className="text-xs text-slate-500 uppercase">{attempt.difficulty}</div>
                            <div className="text-sm text-slate-300">
                                {new Date(attempt.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-white">{attempt.final_score} pts</div>
                            <div className="text-xs text-emerald-400">{Math.round(attempt.net_wpm)} WPM</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No attempts yet. Start testing!
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
